// components/JsonLd.tsx
"use client";

import { useLayoutEffect } from "react";

function toJsonLdString(data: object | object[]): string {
  // Flatten nested arrays so [breadcrumbs, [org, person, ...]] becomes a flat list
  const normalized = Array.isArray(data) ? data.flat() : data;
  // Escape `<` to prevent breaking the <script>
  return JSON.stringify(normalized).replace(/</g, "\\u003c");
}

/**
 * Renders JSON-LD structured data as a <script type="application/ld+json"> tag.
 *
 * React 19 does not reconcile inline <script> nodes during hydration: the SSR
 * node stays in the DOM and a second copy is inserted. That surfaces as
 * duplicate structured data for crawlers that execute JS (Googlebot / Rich
 * Results Test). Making this a Client Component alone does not prevent it.
 *
 * Fix: still SSR the <script> (present in view-source), then in useLayoutEffect
 * remove every earlier duplicate with the same id, keeping the last (React-owned)
 * copy. Runs before paint so JS-executing crawlers see a single script.
 */
export default function JsonLd({
  data,
  id = "jsonld",
}: {
  data: object | object[] | null | undefined;
  id?: string;
}) {
  const json = data ? toJsonLdString(data) : null;

  useLayoutEffect(() => {
    if (!json) return;

    const matches = document.querySelectorAll(
      `script#${CSS.escape(id)}[type="application/ld+json"]`,
    );
    // SSR leftover comes first; React's hydrated node is last. Drop orphans.
    matches.forEach((node, index) => {
      if (index < matches.length - 1) node.remove();
    });
  }, [id, json]);

  if (!json) return null;

  return (
    <script
      id={id}
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: json }}
      suppressHydrationWarning
    />
  );
}
